<template>
  <page-loader>
    <v-container>
      <v-row no-gutters>
        <v-col class="d-flex align-center justify-start">
          <toggle-input v-model="query.params.publishedOnly" class="mr-4" label="Published Only" />
        </v-col>
        <v-col class="d-flex align-center justify-end">
          <search-input
            v-model="query.searchTerm"
            :disabled="isLoading || hasErrors"
            :is-loading="isLoading"
            class="ml-4"
            hide-details
          />
        </v-col>
      </v-row>
      <v-row class="pt-4" no-gutters>
        <v-col class="d-flex align-center justify-space-between">
          <select-list v-model="query.params.offerType" :items="offerTypes" label="Offer Type" clearable hide-details />
          <v-btn :disabled="isLoading" :loading="isLoading" color="primary" depressed @click="onClickExport">
            Export list as CSV
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-row>
        <v-col v-if="offersList.length">
          <v-card :loading="isLoading" class="pa-2" outlined>
            <v-data-table :headers="headers" :items="offersList">
              <template v-slot:item="{ item }">
                <v-hover v-slot="{ hover }">
                  <tr>
                    <td>
                      {{ item.name }}
                      <template v-if="!query.params.offerType"
                        >({{ formattedOption('offerTypes', item.type) }})</template
                      >
                    </td>
                    <td>
                      {{ getPrettyLandingPageTemplate(item.funnelTemplate) }}
                    </td>
                    <td>
                      {{ getPrettyLandingPageTemplate(item.paidFunnelTemplate) }}
                    </td>
                    <td align="right">
                      <div v-show="hover">
                        <more-menu>
                          <v-list-item :to="{ name: 'offers.edit', params: { slug: item.slug } }" link>
                            <v-list-item-title>
                              View Offer
                            </v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            :disabled="!item.funnelTemplate"
                            :inactive="!item.funnelTemplate"
                            :to="{ name: 'landing-pages.content.edit', params: { slug: item.funnelTemplate } }"
                          >
                            <v-list-item-title>View Organic Template</v-list-item-title>
                          </v-list-item>
                          <v-list-item
                            :disabled="!item.paidFunnelTemplate"
                            :inactive="!item.paidFunnelTemplate"
                            :to="{ name: 'landing-pages.content.edit', params: { slug: item.paidFunnelTemplate } }"
                          >
                            <v-list-item-title>View Paid Template</v-list-item-title>
                          </v-list-item>
                        </more-menu>
                      </div>
                      <div v-if="!hover" style="min-width:57px"></div>
                    </td>
                  </tr>
                </v-hover>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
        <v-col v-else>
          <empty-list type="offer" />
        </v-col>
      </v-row>
    </v-container>
  </page-loader>
</template>

<script>
// API
import { landingPages, offers } from '@/services/offers'
// Helpers
import { mapGetters, mapMutations } from 'vuex'
import { exportCSV } from '@/utils/csv'
// Components
import MoreMenu from '@/components/more-menu'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import ToggleInput from '@/components/forms/toggle-input.vue'
import EmptyList from '@/components/empty-list'
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

export default {
  name: 'OfferFunnels',
  components: {
    MoreMenu,
    PageLoader,
    SearchInput,
    SelectList,
    ToggleInput,
    EmptyList,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data() {
    return {
      isLoading: false,
      hasErrors: false,
      offersList: [],
      templates: [],
      offerTypeFilter: '',
      searchKey: 'name',
    }
  },
  computed: {
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    offerTypes() {
      return this.getItemsForOption('offerTypes')
    },
    headers() {
      return [
        {
          text: 'Offer',
          align: 'start',
          value: 'name',
        },
        {
          text: 'Organic Funnel Template',
          align: 'start',
          value: 'funnelTemplate',
        },
        {
          text: 'Paid Funnel Template',
          align: 'start',
          value: 'paidFunnelTemplate',
        },
        {
          text: '',
          align: 'end',
          sortable: false,
          value: 'actions',
        },
      ]
    },
  },
  async created() {
    this.$set(this.query.params, 'offerType', 'crypto-robots')
    await Promise.all([this.getOffers(), this.getLandingPages()])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    async getOffers() {
      this.isLoading = true

      try {
        const { data: offersResponse } = await offers.getAllOffers({
          compact: true,
          properties: ['funnelTemplate', 'paidFunnelTemplate'],
          filters: this.queryFilters,
        })
        this.offersList = offersResponse
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve offers',
        })
      }

      this.isLoading = false
    },
    async getLandingPages() {
      this.isLoading = true

      try {
        this.templates = await landingPages.getAllLandingPageTemplates()
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve pages',
        })
      }

      this.isLoading = false
    },
    async handleQueryUpdate() {
      await this.getOffers()
    },
    onClickExport() {
      this.isLoading = true

      const headers = ['name', 'offer-type', 'slug', 'funnel-template', 'paid-funnel-template'].join(',')
      const body = this.offersList.map(({ name, type, slug, funnelTemplate, paidFunnelTemplate }) => [
        name,
        type,
        slug,
        funnelTemplate,
        paidFunnelTemplate,
      ])

      const csv = [headers].concat(body).join('\n')

      exportCSV(csv, 'funnel-template-export')

      this.isLoading = false
    },
    getPrettyLandingPageTemplate(templateSlug) {
      if (!templateSlug) return
      const match = this.templates.find(({ slug }) => slug === templateSlug)
      return match?.template?.default?.templateName
    },
  },
}
</script>
